import { Visibility, GetVisibilityRequest, GetVisibilityResponse } from '@zoocasa/go-search';
import { ZOOCASA_TENANT_HEADER_NAME } from 'constants/headers';
import { FetchOptions } from 'types/fetchWithRetry';
import { HttpRequestMethodTypes } from 'types/HttpRequestMethodTypes';
import { fetchWithRetry } from 'utils/fetchWithRetry';
import { getGoSearchHost, isServerSide } from 'utils/host-config';

const GET_VISIBILITY_ENDPOINT_URL = '/api/visibility';

/**
 * A selector for a visibility.
 *
 * NOTE: Only one of the properties should be provided.
 *
 * @property slug The slug of the visibility.
 * @property id The id of the visibility.
 * @property listingKey The listing key of the visibility.
 * @property mls The MLS of the visibility.
 */
export type GetVisibilitySelectorOneOf = {
  slug?: string;
  id?: number;
  listingKey?: string;
  mls?: string;
};

/**
 * Fetches visibility information for a listing using various selector methods.
 *
 * @param selector The selector object containing one of the following properties:
 *   - `slug`: The URL slug of the listing
 *   - `id`: The numeric ID of the listing
 *   - `listingKey`: The listing key identifier
 *   - `mls`: The MLS identifier
 * @param signal Optional AbortSignal to cancel the request
 * @param host Optional host URL for the API endpoint
 *
 * @returns A promise that resolves to:
 *   - An array of Visibility objects if the request is successful
 *   - undefined if:
 *     - No valid selector is provided
 *     - The request fails
 *     - The request is aborted
 *     - The response is not OK
 *
 * @example
 * // Using slug selector
 * const visibility = await getVisibility({ slug: '123-main-st' });
 *
 * @example
 * // Using ID selector with abort signal
 * const controller = new AbortController();
 * const visibility = await getVisibility({ id: 12345 }, controller.signal);
 */
export async function getVisibility(selector: GetVisibilitySelectorOneOf, tenantId?: string, signal?: AbortSignal, host: string = getGoSearchHost(isServerSide())): Promise<Visibility | undefined> {
  if (!selector.slug && !selector.id && !selector.listingKey && !selector.mls) {
    return undefined;
  }

  const getVisibilityEndpointUrl = `${host}${GET_VISIBILITY_ENDPOINT_URL}`;
  try {
    let body: GetVisibilityRequest;
    let headers: FetchOptions['headers'];
    if (selector.slug) {
      body = { slug: selector.slug };
    } else if (selector.id) {
      body = { id: selector.id };
    } else if (selector.listingKey) {
      body = { listingKey: selector.listingKey };
    } else if (selector.mls) {
      body = { mls: selector.mls };
    }

    if (tenantId) {
      body.tenantId = tenantId;
      headers = { [ZOOCASA_TENANT_HEADER_NAME]: tenantId };
    }

    const request = GetVisibilityRequest.fromJSON(body);
    const encodedBody = GetVisibilityRequest.encode(request).finish();

    const options: FetchOptions = {
      method: HttpRequestMethodTypes.POST,
      headers,
      body: encodedBody,
      signal,
    };
    const response = await fetchWithRetry(getVisibilityEndpointUrl, options);
    if (response.ok) {
      const content = await response.blob();
      const buffer = await content.arrayBuffer();
      const apiResp = GetVisibilityResponse.decode(new Uint8Array(buffer));
      return apiResp.visibility;
    } else {
      console.error(
        `fetch visibility: ${response.status} ${response.statusText} ${response.type}`
      );
      return undefined;
    }
  } catch (error: unknown) {
    if (error instanceof Error && error.name === 'AbortError') {
      console.debug('fetch visibility request was aborted');
    } else {
      console.error('failed to fetch visibility: %s', error);
    }
    return undefined;
  }
}